import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocale } from '../contexts/LocaleContext';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE } from '../config';
import { Link2, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogPrimaryShortcut,
} from './ui/dialog';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { CancelButton } from './ui/cancel-button';
import { Link } from './ui/link';
import { TierLimitModal } from './TierLimitModal';
import {
  fetchExistingProjectResources,
  type ExistingProjectResource,
} from '../utils/check-existing-resources';
import { parseResourceUrl } from '../utils/ensure-https-url';
import { handleModifierAwareNavigate } from '../utils/app-navigation';

interface AddLinkModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
}

function existingResourceLabel(resource: ExistingProjectResource): string {
  return resource.title.trim() || resource.fileName.trim() || resource.url?.trim() || resource.id;
}

export const AddLinkModal: React.FC<AddLinkModalProps> = ({
  isOpen,
  onClose,
  projectId,
}) => {
  const { t } = useLocale();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [url, setUrl] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showResourceLimitModal, setShowResourceLimitModal] = useState(false);
  const [showDuplicateConfirm, setShowDuplicateConfirm] = useState(false);
  const [existingResources, setExistingResources] = useState<ExistingProjectResource[]>([]);
  const isOpenRef = useRef(isOpen);
  const showDuplicateConfirmRef = useRef(false);
  isOpenRef.current = isOpen;

  const updateShowDuplicateConfirm = (open: boolean): void => {
    showDuplicateConfirmRef.current = open;
    setShowDuplicateConfirm(open);
  };

  useEffect(() => {
    if (isOpen) {
      setUrl('');
      setError(null);
      setIsSubmitting(false);
      showDuplicateConfirmRef.current = false;
      setShowDuplicateConfirm(false);
      setExistingResources([]);
    }
  }, [isOpen]);

  const createLink = async (): Promise<void> => {
    if (!token) {
      setError(t('not_authenticated'));
      return;
    }

    const resolvedUrl = parseResourceUrl(url);
    if (!resolvedUrl) {
      setError(t('invalid_url'));
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch(
        `${API_BASE}/api/projects/${projectId}/resources/link`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ url: resolvedUrl }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 403 && errorData.errorCode === 'RESOURCE_LIMIT_REACHED') {
          setShowResourceLimitModal(true);
          setIsSubmitting(false);
          return;
        }
        throw new Error(errorData.error || t('failed_to_add_link'));
      }

      const data = await response.json();
      window.dispatchEvent(
        new CustomEvent('resource-uploaded', {
          detail: { resourceIds: [data.id], projectId },
        })
      );
      onClose();
    } catch (err) {
      console.error('Error adding link:', err);
      setError(err instanceof Error ? err.message : t('failed_to_add_link'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();

    if (!url.trim()) {
      setError(t('url_required'));
      return;
    }

    const resolvedUrl = parseResourceUrl(url);
    if (!resolvedUrl) {
      setError(t('invalid_url'));
      return;
    }

    if (!token) {
      setError(t('not_authenticated'));
      return;
    }

    setIsSubmitting(true);
    setError(null);

    const existing = await fetchExistingProjectResources(token, projectId, {
      url: resolvedUrl,
    });

    if (!isOpenRef.current) {
      return;
    }

    if (existing.length > 0) {
      setIsSubmitting(false);
      setExistingResources(existing);
      updateShowDuplicateConfirm(true);
      return;
    }

    await createLink();
  };

  return (
    <>
      <Dialog
        open={isOpen}
        onOpenChange={(open) => {
          if (open || isSubmitting) {
            return;
          }
          if (showDuplicateConfirmRef.current) {
            updateShowDuplicateConfirm(false);
            return;
          }
          onClose();
        }}
      >
        <DialogContent className="max-w-md">
          {showDuplicateConfirm ? (
            <>
              <DialogHeader>
                <DialogTitle>{t('duplicate_resource_link_title')}</DialogTitle>
                <DialogDescription>
                  {t('duplicate_resource_link_description')}
                </DialogDescription>
              </DialogHeader>
              {existingResources.length > 0 && (
                <ul
                  className="mb-5 max-h-[40vh] list-none space-y-2 overflow-y-auto rounded-md border p-3"
                  aria-label={t('duplicate_resource_existing_list')}
                >
                  {existingResources.map((resource) => {
                    const path = `/projects/${projectId}/resources/${resource.id}`;
                    const label = existingResourceLabel(resource);
                    return (
                      <li key={resource.id}>
                        <Link
                          to={path}
                          className="flex w-full items-start gap-2 text-left text-sm text-primary hover:underline"
                          onClick={(e) => {
                            handleModifierAwareNavigate(e, navigate, path, {
                              afterSameTabNavigate: onClose,
                            });
                          }}
                          onAuxClick={(e) => {
                            handleModifierAwareNavigate(e, navigate, path);
                          }}
                        >
                          <Link2 className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                          <span className="min-w-0 flex-1 break-words">{label}</span>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              )}
              {error && (
                <p className="mb-5 text-sm text-destructive">{error}</p>
              )}
              <DialogFooter>
                <CancelButton
                  type="button"
                  onClick={() => updateShowDuplicateConfirm(false)}
                  disabled={isSubmitting}
                />
                <Button
                  type="button"
                  disabled={isSubmitting}
                  dialogPrimary
                  className="inline-flex items-center gap-1.5"
                  onClick={() => {
                    void createLink();
                  }}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t('adding')}
                    </>
                  ) : (
                    <>
                      {t('duplicate_resource_add_anyway')}
                      <DialogPrimaryShortcut className="ml-0.5" />
                    </>
                  )}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>{t('add_link')}</DialogTitle>
                <DialogDescription>
                  {t('add_link_description')}
                </DialogDescription>
              </DialogHeader>

              <form onSubmit={handleSubmit}>
                <div className="flex flex-col gap-4 py-4">
                  <div className="flex items-center gap-3">
                    <Link2 className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                    <Input
                      type="text"
                      inputMode="url"
                      autoCapitalize="none"
                      autoCorrect="off"
                      spellCheck={false}
                      placeholder={t('add_link_url_placeholder')}
                      value={url}
                      onChange={(e) => {
                        setUrl(e.target.value);
                        setError(null);
                      }}
                      disabled={isSubmitting}
                      autoFocus
                    />
                  </div>

                  {error && (
                    <p className="text-sm text-destructive">{error}</p>
                  )}
                </div>

                <DialogFooter>
                  <CancelButton
                    type="button"
                    onClick={onClose}
                    disabled={isSubmitting}
                  />
                  <Button
                    type="submit"
                    disabled={isSubmitting || !url.trim()}
                    dialogPrimary
                    className="inline-flex items-center gap-1.5"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {t('adding')}
                      </>
                    ) : (
                      <>
                        {t('add')}
                        <DialogPrimaryShortcut className="ml-0.5" />
                      </>
                    )}
                  </Button>
                </DialogFooter>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>
      <TierLimitModal
        open={showResourceLimitModal}
        onOpenChange={setShowResourceLimitModal}
        limitType="resource"
      />
    </>
  );
};
